import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, ArrowDownRight, Minus, Users, Loader2, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { useAuth } from '../../contexts/AuthContext';

const money = (n) => {
  const v = Math.round(Number(n) || 0);
  const sign = v < 0 ? '-' : '';
  const abs = Math.abs(v);
  if (abs >= 10000) return `${sign}$${(abs / 1000).toFixed(abs % 1000 === 0 ? 0 : 1)}k`;
  return `${sign}$${abs.toLocaleString()}`;
};

const monthName = (key) => {
  if (!key) return '';
  const [y, m] = key.split('-').map(Number);
  return new Date(y, (m || 1) - 1, 1).toLocaleString('en-US', { month: 'short' });
};

function DeltaPill({ delta, deltaPct }) {
  const flat = Math.abs(Number(delta) || 0) < 1;
  const up = (delta || 0) > 0;
  const Icon = flat ? Minus : up ? ArrowUpRight : ArrowDownRight;
  const tone = flat
    ? 'bg-slate-100 text-slate-500'
    : up
      ? 'bg-emerald-50 text-emerald-700'
      : 'bg-red-50 text-red-700';
  return (
    <span
      className={`inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-[11px] font-semibold ${tone}`}
      data-testid="agent-margin-delta"
    >
      <Icon className="h-3 w-3" />
      {flat ? 'flat' : `${up ? '+' : '−'}${money(Math.abs(delta))}${deltaPct != null ? ` · ${Math.abs(deltaPct)}%` : ''}`}
    </span>
  );
}

function GoalBar({ pct }) {
  if (pct == null) return <span className="text-[11px] text-slate-400">no goal set</span>;
  const capped = Math.max(0, Math.min(pct, 100));
  const tone = pct >= 100 ? 'bg-emerald-500' : pct >= 60 ? 'bg-amber-500' : 'bg-red-500';
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-full max-w-[90px] rounded-full bg-slate-200 overflow-hidden">
        <div className={`h-full rounded-full ${tone}`} style={{ width: `${capped}%` }} />
      </div>
      <span className="text-[11px] font-medium text-slate-600 tabular-nums">{Math.round(pct)}%</span>
    </div>
  );
}

/**
 * Agent-by-agent margin: this month so far against last month's full total,
 * plus progress on each rep's monthly goal. Backend scopes the rows, so a
 * sales rep only ever sees their own line.
 */
export function AgentMarginPanel() {
  const { token, API } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/margin-goals/agent-comparison`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data: res }) => alive && setData(res))
      .catch((err) => {
        if (!alive) return;
        setError(err?.response?.data?.detail || 'Agent margin is unavailable right now');
      })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [API, token]);

  const rows = data?.rows || [];
  const totals = data?.totals || {};

  return (
    <Card className="border-slate-200" data-testid="agent-margin-section">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <CardTitle className="text-base font-semibold">Agent Margin</CardTitle>
            <p className="text-xs text-slate-500 mt-0.5">
              {monthName(data?.month) || 'This month'} so far vs all of {monthName(data?.prev_month) || 'last month'}
            </p>
          </div>
          <Link
            to="/accounting/profit-aggregations"
            className="text-xs font-medium text-blue-600 hover:text-blue-700 flex items-center gap-1"
            data-testid="agent-margin-view-all"
          >
            Margins <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {loading ? (
          <div className="flex items-center justify-center py-10 text-slate-400">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : error ? (
          <div className="text-center py-8" data-testid="agent-margin-error">
            <AlertTriangle className="h-8 w-8 text-amber-400 mx-auto mb-3" />
            <p className="text-sm text-slate-600">{error}</p>
          </div>
        ) : rows.length === 0 ? (
          <div className="text-center py-8" data-testid="agent-margin-empty">
            <Users className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">No agent margin booked yet this month</p>
          </div>
        ) : (
          <div className="overflow-x-auto -mx-6">
            <div className="w-full min-w-[620px]">
              <div className="grid grid-cols-[1.4fr_110px_110px_120px_1fr] items-center gap-3 border-y border-slate-100 bg-slate-50/50 py-2 px-6">
                <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Agent</div>
                <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider text-right">
                  {monthName(data?.month)} so far
                </div>
                <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider text-right">
                  All of {monthName(data?.prev_month)}
                </div>
                <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider text-right">Change</div>
                <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Monthly goal</div>
              </div>

              {rows.map((r, idx) => (
                <div
                  key={r.user_id}
                  className={`grid grid-cols-[1.4fr_110px_110px_120px_1fr] items-center gap-3 px-6 py-2.5 hover:bg-slate-50 transition-colors ${
                    idx === rows.length - 1 ? '' : 'border-b border-slate-100'
                  }`}
                  data-testid={`agent-margin-row-${r.user_id}`}
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{r.name}</p>
                    <p className="text-[11px] text-slate-400">
                      {r.loads} load{r.loads === 1 ? '' : 's'}
                      {r.pipeline_margin > 0 ? ` · ${money(r.pipeline_margin)} in pipeline` : ''}
                    </p>
                  </div>
                  <div
                    className="text-sm font-semibold text-slate-900 mono text-right tabular-nums"
                    data-testid={`agent-margin-current-${r.user_id}`}
                  >
                    {money(r.recognised_margin)}
                  </div>
                  <div className="text-sm text-slate-500 mono text-right tabular-nums">
                    {money(r.prev_recognised_margin)}
                  </div>
                  <div className="text-right">
                    <DeltaPill delta={r.delta} deltaPct={r.delta_pct} />
                  </div>
                  <div><GoalBar pct={r.pct_of_goal} /></div>
                </div>
              ))}

              {rows.length > 1 && (
                <div className="grid grid-cols-[1.4fr_110px_110px_120px_1fr] items-center gap-3 px-6 py-2.5 border-t border-slate-200 bg-slate-50/60">
                  <div className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Team</div>
                  <div className="text-sm font-bold text-slate-900 mono text-right tabular-nums" data-testid="agent-margin-total-current">
                    {money(totals.recognised_margin)}
                  </div>
                  <div className="text-sm text-slate-500 mono text-right tabular-nums">
                    {money(totals.prev_recognised_margin)}
                  </div>
                  <div className="text-right">
                    <DeltaPill
                      delta={(totals.recognised_margin || 0) - (totals.prev_recognised_margin || 0)}
                      deltaPct={totals.prev_recognised_margin > 0
                        ? Math.round((((totals.recognised_margin || 0) - totals.prev_recognised_margin) / totals.prev_recognised_margin) * 1000) / 10
                        : null}
                    />
                  </div>
                  <div>
                    <GoalBar
                      pct={totals.goal > 0
                        ? Math.round(((totals.recognised_margin || 0) / totals.goal) * 1000) / 10
                        : null}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default AgentMarginPanel;
